import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Download,
  Add,
  Remove,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

interface BillingTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  compressionJobId?: string;
  paymentId?: string;
  createdAt: string;
}

interface BillingHistoryProps {
  token: string | null;
}

const BillingHistory: React.FC<BillingHistoryProps> = ({ token }) => {
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/credits/transactions?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transaction history');
      }

      const data = await response.json();
      // Filter for actual monetary transactions only (purchases with payment IDs)
      const billingTransactions = data.filter((t: BillingTransaction) =>
        t.type === 'Purchase' && t.paymentId
      );
      setTransactions(billingTransactions);
    } catch (err) {
      console.error('Error fetching billing history:', err);
      setError('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    // For now, we'll show credit amounts. In a real app, this would be monetary amounts
    return `${amount} Credits`;
  };

  const getTransactionDescription = (transaction: BillingTransaction) => {
    if (transaction.paymentId) {
      // This would be a credit purchase
      return transaction.description || 'Credit Purchase';
    }
    return transaction.description;
  };

  const handleDownloadInvoice = (transaction: BillingTransaction) => {
    // TODO: Implement invoice download
    console.log('Download invoice for transaction:', transaction.id);
  };

  const toggleRowExpansion = (transactionId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(transactionId)) {
      newExpandedRows.delete(transactionId);
    } else {
      newExpandedRows.add(transactionId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ px: 3, textAlign: 'center', py: 4 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          No billing history found
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Credit purchases and payments will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
        Billing History
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {isMobile ? 'Transaction' : 'Description'}
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                </>
              )}
              {isMobile && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => {
              const isExpanded = expandedRows.has(transaction.id);
              return (
                <React.Fragment key={transaction.id}>
                  <TableRow hover sx={{ cursor: isMobile ? 'pointer' : 'default' }}>
                    <TableCell
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1, sm: 2 } }}
                      onClick={isMobile ? () => toggleRowExpansion(transaction.id) : undefined}
                    >
                      {formatDate(transaction.createdAt)}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1, sm: 2 } }}
                      onClick={isMobile ? () => toggleRowExpansion(transaction.id) : undefined}
                    >
                      {isMobile ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {getTransactionDescription(transaction)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatAmount(Math.abs(transaction.amount))}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2">
                            {getTransactionDescription(transaction)}
                          </Typography>
                          {transaction.type === 'Purchase' && (
                            <Chip
                              label="Purchase"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {transaction.amount > 0 ? (
                              <Add sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <Remove sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography
                              sx={{
                                color: transaction.amount > 0 ? 'success.main' : 'error.main',
                                fontWeight: 500
                              }}
                            >
                              {formatAmount(Math.abs(transaction.amount))}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                          {transaction.paymentId ? (
                            <Button
                              size="small"
                              startIcon={<Download />}
                              onClick={() => handleDownloadInvoice(transaction)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 500
                              }}
                            >
                              Download
                            </Button>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                    {isMobile && (
                      <TableCell sx={{ width: 48, py: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(transaction.id)}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {isMobile && (
                    <TableRow>
                      <TableCell sx={{ py: 0, border: 0 }} colSpan={3}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1, backgroundColor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {transaction.amount > 0 ? (
                                  <Add sx={{ color: 'success.main', fontSize: 16 }} />
                                ) : (
                                  <Remove sx={{ color: 'error.main', fontSize: 16 }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: transaction.amount > 0 ? 'success.main' : 'error.main',
                                    fontWeight: 500
                                  }}
                                >
                                  {formatAmount(Math.abs(transaction.amount))}
                                </Typography>
                              </Box>
                              {transaction.type === 'Purchase' && (
                                <Chip
                                  label="Purchase"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {transaction.paymentId ? (
                              <Button
                                size="small"
                                startIcon={<Download />}
                                onClick={() => handleDownloadInvoice(transaction)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500
                                }}
                              >
                                Download Invoice
                              </Button>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No invoice available
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BillingHistory;
